import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ClinicalNoteModal } from '@/components/ClinicalNoteModal';
import { FormattedClinicalText } from '@/components/FormattedClinicalText';
import { Skeleton } from '@/components/ui/skeleton';
import type { ReplitDocument } from '@/types/replit-dashboard';
import ExplainButton from './ExplainButton';
import RewriteView from './RewriteView';
import { useDocumentRewrite } from './useDocumentRewrite';
import { dayGroupId, formatRecordDate, formatRecordDateTime, formatRecordTime, isWithinHoursOf, noteTypeKey } from './records-utils';

interface NotesSectionProps {
  notes: ReplitDocument[];
  onAskQuestion: (question: string, displayText?: string) => void;
  variant: 'main' | 'rail';
}

/** Notes longer than this get an "Open full note" link to the full-screen reader. */
const LONG_NOTE_CHARS = 2000;
const NEW_WINDOW_HOURS = 24;

const timeOf = (note: ReplitDocument) => {
  const t = new Date(note.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
};

function NoteGist({ note, title }: { note: ReplitDocument; title: string }) {
  const { rewrite, isFetching } = useDocumentRewrite('note', String(note.id), title, note.content);
  if (rewrite) return <span className="block text-sm text-text-secondary mt-1">{rewrite.gist}</span>;
  if (isFetching) return <Skeleton className="h-3.5 w-3/4 mt-1.5" data-testid="note-gist-skeleton" />;
  return null;
}

interface NoteDetailProps {
  note: ReplitDocument;
  label: string;
  onBack: () => void;
  onExplain: () => void;
  onAskQuestion: NotesSectionProps['onAskQuestion'];
}

function NoteDetail({ note, label, onBack, onExplain, onAskQuestion }: NoteDetailProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || 'en';
  const [modalOpen, setModalOpen] = useState(false);
  const { rewrite, isFetching, isError, fetch } = useDocumentRewrite('note', String(note.id), note.noteType, note.content);
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div data-testid="note-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-3" data-testid="button-notes-back">
        <ChevronLeft className="h-4 w-4" />
        {t('records.backToAllNotes')}
      </button>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-text-primary">{label}</h3>
          <p className="text-sm text-text-secondary mt-0.5">
            {note.authorName} · {formatRecordDateTime(note.createdAt, language)}
          </p>
        </div>
        <ExplainButton onClick={onExplain} ariaLabel={t('explain.noteSummaryDisplay', { noteType: note.noteType, authorName: note.authorName, date: formatRecordDate(note.createdAt, language) })} testId={`button-explain-note-${note.id}`} />
      </div>
      <RewriteView
        rewrite={rewrite}
        isFetching={isFetching}
        isError={isError}
        originalLabel={t('records.tabOriginal')}
        plainSubline={t('records.plainNoteSubline')}
        originalSubline={t('records.originalNoteSubline')}
        original={<FormattedClinicalText text={note.content} />}
      />
      {note.content.length > LONG_NOTE_CHARS && (
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm text-primary font-medium mt-2"
          data-testid="button-open-full-note"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          {t('records.openFullNote')}
        </button>
      )}
      <ClinicalNoteModal note={note} open={modalOpen} onClose={() => setModalOpen(false)} onAskQuestion={onAskQuestion} />
    </div>
  );
}

export default function NotesSection({ notes, onAskQuestion, variant }: NotesSectionProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || 'en';
  const [openId, setOpenId] = useState<string | null>(null);
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';

  if (!notes || notes.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary">
        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>{t('records.noClinicalNotesAvailable')}</p>
      </div>
    );
  }

  const sorted = notes.slice().sort((a, b) => timeOf(b) - timeOf(a));
  const newest = sorted[0].createdAt;

  const labelFor = (note: ReplitDocument) => {
    const key = noteTypeKey(note.noteType);
    return key ? t(key) : note.noteType;
  };

  const explain = (note: ReplitDocument) => {
    const date = formatRecordDate(note.createdAt, language);
    const params = { noteType: note.noteType, authorName: note.authorName, date };
    onAskQuestion(t('explain.noteSummaryQuestion', { ...params, content: note.content }), t('explain.noteSummaryDisplay', params));
  };

  const openNote = openId !== null ? sorted.find((note) => String(note.id) === openId) : undefined;
  if (openNote) {
    return (
      <NoteDetail
        note={openNote}
        label={labelFor(openNote)}
        onBack={() => setOpenId(null)}
        onExplain={() => explain(openNote)}
        onAskQuestion={onAskQuestion}
      />
    );
  }

  const groups: Array<{ id: string; label: string; notes: ReplitDocument[] }> = [];
  sorted.forEach((note) => {
    const id = dayGroupId(note.createdAt) ?? 'undated';
    let group = groups.find((g) => g.id === id);
    if (!group) {
      const label = id === 'today' ? t('records.dayToday')
        : id === 'yesterday' ? t('records.dayYesterday')
        : id === 'undated' ? t('records.dayUnknown')
        : formatRecordDate(note.createdAt, language);
      group = { id, label, notes: [] };
      groups.push(group);
    }
    group.notes.push(note);
  });

  return (
    <div>
      {groups.map((group) => (
        <div key={group.id} className="mb-4" data-testid={`notes-group-${group.id}`}>
          <h3 className="text-sm font-semibold text-text-primary mb-2">{group.label}</h3>
          <div className="rounded-lg border bg-white divide-y">
            {group.notes.map((note) => {
              const label = labelFor(note);
              const isNew = isWithinHoursOf(note.createdAt, newest, NEW_WINDOW_HOURS);
              return (
                <div key={note.id} className={`flex items-start justify-between gap-3 ${rowPadding} py-2.5 hover:bg-gray-50`} data-testid={`row-note-${note.id}`}>
                  <button onClick={() => setOpenId(String(note.id))} className="text-left flex-1 min-w-0" data-testid={`button-open-note-${note.id}`}>
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-primary">{label}</span>
                      {isNew && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t('records.badgeNew')}</span>
                      )}
                    </span>
                    <span className="block text-sm text-text-secondary mt-0.5">
                      {note.authorName} · {formatRecordTime(note.createdAt, language)}
                    </span>
                    <NoteGist note={note} title={note.noteType} />
                  </button>
                  <span className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                    <ExplainButton
                      onClick={() => explain(note)}
                      ariaLabel={t('explain.noteSummaryDisplay', { noteType: note.noteType, authorName: note.authorName, date: formatRecordDate(note.createdAt, language) })}
                      testId={`button-explain-note-row-${note.id}`}
                    />
                    <ChevronRight className="h-4 w-4 text-text-secondary" />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
